/** Deploys the demo cases (and optionally their portfolio) into any company and environment. */

import type {
  CompanyWithCounts,
  DeployDemoResponse,
  UserWithCognitoStatus,
} from "@api/app-api";
import {
  DEMO_CATALOG,
  DEMO_PORTFOLIO_NAME,
  type DemoCaseId,
  type DemoCatalogStage,
} from "@app-shared/demo/demo-catalog";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api";
import { config, environmentLabels, type Environment } from "../../config";

type LanguageOption = "en" | "de";

const STAGE_LABEL: Record<DemoCatalogStage, string> = {
  proposed: "Proposed",
  evaluating: "Evaluating",
  committed: "Committed",
  executing: "Executing",
};

const STAGE_COLOR: Record<
  DemoCatalogStage,
  "default" | "info" | "warning" | "success"
> = {
  proposed: "default",
  evaluating: "warning",
  committed: "info",
  executing: "success",
};

const ALL_CASE_IDS: DemoCaseId[] = DEMO_CATALOG.map((entry) => entry.id);

interface DemoDeployPageProps {
  environment: Environment;
}

export const DemoDeployPage: React.FC<DemoDeployPageProps> = ({
  environment,
}) => {
  const [targetEnvironment, setTargetEnvironment] =
    useState<Environment>(config.safestEnvironment);
  const [companies, setCompanies] = useState<CompanyWithCounts[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [targetCompanyId, setTargetCompanyId] = useState("");
  const [users, setUsers] = useState<UserWithCognitoStatus[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [targetOwnerId, setTargetOwnerId] = useState("");

  const [selectedCaseIds, setSelectedCaseIds] =
    useState<DemoCaseId[]>(ALL_CASE_IDS);
  const [language, setLanguage] = useState<LanguageOption>("en");
  const [createPortfolio, setCreatePortfolio] = useState(true);
  const [portfolioName, setPortfolioName] = useState(DEMO_PORTFOLIO_NAME.en);

  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<DeployDemoResponse | null>(null);

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    setError(null);
    try {
      apiClient.setEnvironment(targetEnvironment);
      const result = await apiClient.getCompanies();
      setCompanies(result);
    } catch (err) {
      console.error("Failed to load companies:", err);
      setError(err instanceof Error ? err.message : "Failed to load companies");
    } finally {
      // Restore in finally: the client is shared and persists its selection.
      apiClient.setEnvironment(environment);
      setLoadingCompanies(false);
    }
  }, [targetEnvironment, environment]);

  const loadUsers = useCallback(async () => {
    if (!targetCompanyId) {
      setUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      apiClient.setEnvironment(targetEnvironment);
      const result = await apiClient.getCompanyUsers(targetCompanyId);
      setUsers(result.filter((user) => user.status === "ACTIVE"));
    } catch (err) {
      console.error("Failed to load users:", err);
      setUsers([]);
    } finally {
      apiClient.setEnvironment(environment);
      setLoadingUsers(false);
    }
  }, [targetCompanyId, targetEnvironment, environment]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setTargetOwnerId("");
  }, [targetCompanyId]);

  const toggleCase = (id: DemoCaseId) => {
    setSelectedCaseIds((current) =>
      current.includes(id)
        ? current.filter((caseId) => caseId !== id)
        : [...current, id],
    );
  };

  const allSelected = selectedCaseIds.length === ALL_CASE_IDS.length;

  // The proposed name follows the language until someone types their own.
  const changeLanguage = (next: LanguageOption) => {
    if (portfolioName === DEMO_PORTFOLIO_NAME[language])
      setPortfolioName(DEMO_PORTFOLIO_NAME[next]);
    setLanguage(next);
  };

  const handleDeploy = async () => {
    setDeploying(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await apiClient.deployDemo({
        targetEnvironment,
        companyId: targetCompanyId,
        ownerId: targetOwnerId,
        caseIds: selectedCaseIds,
        portfolioName: createPortfolio ? portfolioName.trim() : null,
        language,
      });
      setSuccess(result);
    } catch (err) {
      console.error("Failed to deploy demo projects:", err);
      setError(
        err instanceof Error ? err.message : "Failed to deploy demo projects",
      );
    } finally {
      setDeploying(false);
    }
  };

  const isValid =
    targetCompanyId !== "" &&
    targetOwnerId !== "" &&
    selectedCaseIds.length > 0 &&
    (!createPortfolio || portfolioName.trim() !== "");

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Demo Deploy</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Deploy the demo cases of one industrial group into any company. Each
          case arrives calculated with its version history, status moves,
          actuals, summary and interview, dated relative to today. The cases
          are written in the background and appear within a few minutes.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success.message}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {/* Target */}
        <Card sx={{ flex: "1 1 400px", minWidth: 350 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Target
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Where the demo projects are deployed to
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Target Environment</InputLabel>
              <Select
                value={targetEnvironment}
                onChange={(e) => {
                  setTargetEnvironment(e.target.value as Environment);
                  setTargetCompanyId("");
                  setTargetOwnerId("");
                }}
                label="Target Environment"
              >
                {config.environments.map((environment) => (
                  <MenuItem key={environment} value={environment}>
                    {environmentLabels[environment].long}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={companies}
              loading={loadingCompanies}
              getOptionLabel={(option) => option.name}
              value={companies.find((c) => c.id === targetCompanyId) || null}
              onChange={(_e, value) => setTargetCompanyId(value?.id || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Company"
                  helperText="The company the demo projects are created in"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.activeUserCount} active users • {option.tier}
                    </Typography>
                  </Box>
                </li>
              )}
              sx={{ mb: 2 }}
            />

            <Autocomplete
              options={users}
              loading={loadingUsers}
              disabled={!targetCompanyId}
              getOptionLabel={(option) =>
                `${option.firstName || ""} ${option.lastName || ""} (${option.email})`.trim()
              }
              value={users.find((u) => u.id === targetOwnerId) || null}
              onChange={(_e, value) => setTargetOwnerId(value?.id || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Owner"
                  helperText="Who will own the demo projects and the portfolio"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">
                      {option.firstName || ""} {option.lastName || ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              )}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Content Language</InputLabel>
              <Select
                value={language}
                onChange={(e) => changeLanguage(e.target.value as LanguageOption)}
                label="Content Language"
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="de">German (Deutsch)</MenuItem>
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={createPortfolio}
                  onChange={(e) => setCreatePortfolio(e.target.checked)}
                />
              }
              label="Bundle the published cases into a portfolio"
            />
            {createPortfolio && (
              <TextField
                fullWidth
                label="Portfolio Name"
                value={portfolioName}
                onChange={(e) => setPortfolioName(e.target.value)}
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>

        {/* Templates */}
        <Card sx={{ flex: "1 1 500px", minWidth: 400 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                Cases ({selectedCaseIds.length}/{ALL_CASE_IDS.length})
              </Typography>
              <Button
                size="small"
                onClick={() => setSelectedCaseIds(allSelected ? [] : ALL_CASE_IDS)}
              >
                {allSelected ? "Deselect all" : "Select all"}
              </Button>
            </Box>
            <List dense>
              {DEMO_CATALOG.map((entry) => (
                <ListItem key={entry.id} disablePadding>
                  <ListItemButton onClick={() => toggleCase(entry.id)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedCaseIds.includes(entry.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="body2">
                            {entry.name[language]}
                          </Typography>
                          <Chip
                            label={STAGE_LABEL[entry.stage]}
                            size="small"
                            color={STAGE_COLOR[entry.stage]}
                            variant="outlined"
                          />
                          {entry.closed && (
                            <Chip label="Closed" size="small" variant="outlined" />
                          )}
                          {!entry.published && (
                            <Chip label="Unpublished" size="small" variant="outlined" />
                          )}
                          {entry.hasActuals && (
                            <Chip
                              label="Actuals"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={entry.blurb[language]}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Deploy */}
        <Card sx={{ flex: "1 1 100%", minWidth: 350 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={
                  deploying ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RocketLaunchIcon />
                  )
                }
                onClick={handleDeploy}
                disabled={!isValid || deploying}
              >
                {deploying
                  ? "Deploying..."
                  : `Deploy ${selectedCaseIds.length} case(s)`}
              </Button>
              <Typography variant="body2" color="text.secondary">
                Creates fresh cases on every deploy, so running it again adds a
                second set.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
